import type { ValidationIssue } from "./generated/contracts";
import type { ProblemDetailsBody } from "./problemDetails";

/**
 * The kind of refusal, derived from the HTTP status per the D-204 status-code table
 * (ApiExceptionFilter.cs / EvergreenController.cs). Every non-2xx response from the API becomes
 * one of these, so a component never has to remember what a raw status number means.
 */
export type ApiErrorKind =
  | "unauthenticated" // 401 - the presented credential was refused; re-authenticate.
  | "accessDenied" // 403 - AccessDeniedException; carries reasonCode + operation.
  | "notFound" // 404
  | "conflict" // 409 - a genuine concurrency refusal (e.g. AssignRoleAsync, D-161).
  | "validationFailed" // 422 - PublishRefusedException / IngestionRefusedException / a refused SaveResult.
  | "badRequest" // 400
  | "payloadTooLarge" // 413 - D-218's upload cap.
  | "sourceUnhealthy" // 503 - INGEST_SOURCE_UNHEALTHY.
  | "notImplemented" // 501
  | "cancelled" // 499 (non-standard; the caller withdrew the request).
  | "serverError" // 500 and anything else unmapped.
  | "network"; // fetch itself failed (offline, DNS, CORS) - never reached the API.

/** A typed refusal from the Evergreen Portfolio API, built from its RFC 7807 problem body. */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly reasonCode: string | undefined;
  readonly operation: string | undefined;
  readonly issues: ValidationIssue[];
  readonly correlationId: string | undefined;

  constructor(kind: ApiErrorKind, status: number, message: string, problem?: ProblemDetailsBody) {
    super(message);
    this.name = "ApiError";
    this.kind = kind;
    this.status = status;
    this.reasonCode = problem?.reasonCode;
    this.operation = problem?.operation;
    this.issues = problem?.issues ?? [];
    this.correlationId = problem?.correlationId;
  }
}

/** Maps an HTTP status to an ApiErrorKind, mirroring ApiExceptionFilter's D-204 table. */
export function kindForStatus(status: number): ApiErrorKind {
  switch (status) {
    case 400:
      return "badRequest";
    case 401:
      return "unauthenticated";
    case 403:
      return "accessDenied";
    case 404:
      return "notFound";
    case 409:
      return "conflict";
    case 413:
      return "payloadTooLarge";
    case 422:
      return "validationFailed";
    case 499:
      return "cancelled";
    case 501:
      return "notImplemented";
    case 503:
      return "sourceUnhealthy";
    default:
      return status >= 500 ? "serverError" : "badRequest";
  }
}
